import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./SearchResultsFilterSideBar.module.scss";
import AppGuidelineItem from "../AppSearchFilters/AppGuidelineItem";

const normalizeSearchTerm = (term) =>
  term
    .split(/\s+/)
    .map((t) => t.trim().replace(/^\(+|\)+$/g, ""))
    .filter(Boolean)
    .filter((t) => t.toUpperCase() !== "AND")
    .filter((t, i, arr) => arr.indexOf(t) === i);

const EnhancedSearchFilterSideBar = ({
  search,
  navigate,
  setShowFilterBar,
  setFilterNumber,
  allGuidelines,
  searchWithin,
  setSearchWithin,
  selectedGuidelines,
  setSelectedGuidelines,
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const filterDisplayAmount = 5;

  useEffect(() => {
    const normalizedWithin = normalizeSearchTerm(searchWithin);
    if (normalizedWithin.length) setIsSearchExpanded(true);
  }, [searchWithin]);

  const handleApplyFilter = () => {
    const normalizedWithin = normalizeSearchTerm(searchWithin);
    const searchParams = new URLSearchParams(search);
    const urlSearch = searchParams.get("appendedinputbuttons") || "";

    const newParams = new URLSearchParams();
    newParams.append("appendedinputbuttons", urlSearch);

    selectedGuidelines.forEach((g) => newParams.append("guidelinefilter", g));
    if (normalizedWithin.length) newParams.append("searchWithin", normalizedWithin.join(" "));

    navigate(`/searchAction/?${newParams.toString()}`);
  };

  const handleClearAll = () => {
    setSelectedGuidelines([]);
    setFilterNumber(0);
    setSearchWithin("");

    const searchParams = new URLSearchParams(search);
    const originalSearch = searchParams.get("appendedinputbuttons") || "";

    navigate(`/searchAction/?appendedinputbuttons=${originalSearch}`);
  };

  return (
    <div id="filter-container" className={styles.sidebar}>
      <div className={styles.headerRow}>
        <button onClick={() => setShowFilterBar(false)} className={styles.closeButton}>
          <i className="tg-icons-Cross"></i>
        </button>
        <span className={styles.title}>Filters</span>
        <button onClick={handleClearAll} className={styles.clearButton}>
          Clear all
        </button>
      </div>

      <div className={classNames(styles.section, styles.searchWithin)}>
        <div className={styles.searchWithinTitle}>
          <span className={styles.subheading}>Search within results</span>
          <button
            className={styles.toggleButton}
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            data-testid="search-within-result"
          >
            {isSearchExpanded ? (
              <i className="fa banner-head-topic fa-angle-down"></i>
            ) : (
              <i className="fa banner-head-topic fa-angle-up"></i>
            )}
          </button>
        </div>

        {isSearchExpanded && (
          <div className={styles.searchContent}>
            <label className={styles.searchLabel}>
              Type keywords to refine your current results:
            </label>
            <input
              type="text"
              className={styles.searchInput}
              value={searchWithin}
              onChange={(e) => {
                setSearchWithin(e.target.value);
              }}
              placeholder="e.g treatment, symptoms"
            />
          </div>
        )}
      </div>

      <div className={classNames(styles.section, styles.filterSection)}>
        <div className={classNames("bold", styles.guidelineHeader)}>
          <span>Filter by guideline</span>
        </div>
        <div className={styles.filters}>
          <ul className={styles.guidelineList}>
            {Object.entries(allGuidelines).length ? (
              <div>
                {isFilterExpanded ? (
                  <div>
                    {Object.entries(allGuidelines)
                      .sort(([, a], [, b]) => b - a)
                      .map(([title, count]) => (
                        <AppGuidelineItem
                          guideline={title}
                          resultCount={count || 0}
                          selectedGuideline={selectedGuidelines}
                          key={title}
                          updateSelected={setSelectedGuidelines}
                          data-testid="guideline-item"
                        />
                      ))}
                    <button
                      className={styles.toggleButton}
                      onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                      data-testid="search-within-result"
                    >
                      <div className={styles.showMore}>
                        Show fewer <i className="fa banner-head-topic fa-angle-up"></i>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div>
                    {Object.entries(allGuidelines)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, filterDisplayAmount)
                      .map(([title, count]) => (
                        <AppGuidelineItem
                          guideline={title}
                          resultCount={count || 0}
                          selectedGuideline={selectedGuidelines}
                          key={title}
                          updateSelected={setSelectedGuidelines}
                          data-testid="guideline-item"
                        />
                      ))}
                    {Object.entries(allGuidelines).length > filterDisplayAmount && (
                      <button
                        className={styles.toggleButton}
                        onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        data-testid="search-within-result"
                      >
                        <div className={styles.showMore}>
                          Show {Object.entries(allGuidelines).length - filterDisplayAmount} more{" "}
                          <i className="fa banner-head-topic fa-angle-down"></i>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.searchContent}>
                <label className={styles.searchLabel}>No guidelines found</label>
              </div>
            )}
          </ul>
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.applyButton} onClick={handleApplyFilter}>
          Apply filters
        </button>
      </div>
    </div>
  );
};

EnhancedSearchFilterSideBar.propTypes = {
  search: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  setShowFilterBar: PropTypes.func.isRequired,
  setFilterNumber: PropTypes.func.isRequired,
  allGuidelines: PropTypes.object.isRequired,
  searchWithin: PropTypes.string.isRequired,
  setSearchWithin: PropTypes.func.isRequired,
  selectedGuidelines: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedGuidelines: PropTypes.func.isRequired,
};

export default EnhancedSearchFilterSideBar;
